import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import {
  ecosystemPillars,
  fragmentedPlatforms,
  impactStories,
  learnerJourney,
  philosophyContrast,
  PLAY_STORE_URL,
  roadmap,
  values,
} from "@/data/aboutContent";
import { navigateToFlutterSignup } from "@/utils/flutterAppNavigation";
import { useMarketingAssets, useMarketingImage } from "@/lib/marketingAssets";
import aboutHero from "../../assets/landing/about us.jpg";
import studentsImage from "../../assets/landing/students.jpg";

const TESTIMONIAL_ASSET_KEYS: Record<string, string> = {
  Chungu: "about.testimonial.chungu",
  Florence: "about.testimonial.florence",
  Ackim: "about.testimonial.ackim",
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-skutopia-500 mb-4">
    {children}
  </p>
);

export const AboutHero = () => {
  const heroUrl = useMarketingImage("about.hero.background", aboutHero);
  return (
  <section className="relative min-h-screen flex items-end bg-skutopia-900 overflow-hidden">
    <div className="absolute inset-0">
      <img src={heroUrl} alt="" className="w-full h-full object-cover scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-skutopia-900/90 via-skutopia-900/45 to-skutopia-800/10" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(102,165,147,0.15),transparent_55%)]" />
    </div>

    <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-32">
      <motion.div initial="hidden" animate="visible" className="max-w-4xl">
        <motion.p variants={fadeUp} custom={0} className="text-skutopia-200 text-sm font-medium tracking-widest uppercase mb-6">
          About Skutopia
        </motion.p>
        <motion.h1
          variants={fadeUp}
          custom={1}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-8"
        >
          The high school learning
          <br />
          <span className="text-skutopia-200">and opportunity app.</span>
        </motion.h1>
        <motion.p variants={fadeUp} custom={2} className="text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed mb-10">
          We help African secondary students study with courses, videos, and Muzanga, and find
          scholarships and programmes partners publish on Discover.
        </motion.p>
        <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-4">
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full px-8 py-4 text-base font-medium bg-white text-skutopia-800 hover:bg-skutopia-50 transition-colors"
          >
            Get the app
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50"
      >
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </motion.div>
    </div>
  </section>
  );
};

export const AboutProblem = () => (
  <section className="py-24 sm:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <SectionLabel>Why we started</SectionLabel>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
            Studying and finding opportunities should not be a scavenger hunt.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            High school students already juggle exams, applications, and life. They should not also
            stitch together five disconnected tools for learning and opportunity.
          </p>
          <p className="text-gray-500 leading-relaxed">
            Skutopia puts both in one app, so effort in class can turn into next steps you can take.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="space-y-4"
        >
          {fragmentedPlatforms.map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="p-5 rounded-2xl bg-gray-50 border border-gray-100 text-gray-700 text-sm sm:text-base leading-relaxed"
            >
              {line}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

export const AboutWhy = () => (
  <section className="py-24 sm:py-32 bg-gradient-to-b from-skutopia-50 to-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <SectionLabel>Our mission</SectionLabel>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-8">
          Help secondary students learn well and see what is possible next.
        </h2>
        <p className="text-xl text-gray-600 leading-relaxed mb-6">
          We build for Grades 8 to 12 and the years right after: curriculum-aligned study, AI tutoring,
          peer community, and Discover fed by partner organisations.
        </p>
        <p className="text-lg text-gray-500 leading-relaxed">
          We are clear about what students can use today and what we are still building.
        </p>
      </motion.div>
    </div>
  </section>
);

export const AboutHowItWorks = () => {
  const [active, setActive] = useState(0);
  const pillar = ecosystemPillars[active];

  return (
    <section className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <SectionLabel>How the app works today</SectionLabel>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
            Five tabs students open every day.
          </h2>
          <p className="text-lg text-gray-600">
            This is what students use in the app today.
          </p>
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-skutopia-500 to-skutopia-700 text-white shadow-xl"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-2xl bg-white/15 backdrop-blur">
              <pillar.icon className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">{pillar.title}</h3>
              <p className="text-white/90 text-lg mb-3">{pillar.description}</p>
              <p className="text-skutopia-100 italic">{pillar.why}</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {ecosystemPillars.map((p, i) => (
            <button
              key={p.title}
              type="button"
              onClick={() => setActive(i)}
              className={`group p-4 rounded-2xl text-left transition-all duration-300 border ${
                active === i
                  ? "bg-skutopia-500 text-white border-skutopia-500 shadow-lg scale-[1.02]"
                  : "bg-white/60 backdrop-blur border-gray-200 hover:border-skutopia-300 hover:shadow-md text-gray-800"
              }`}
            >
              <p.icon className={`w-5 h-5 mb-2 ${active === i ? "text-white" : "text-skutopia-500"}`} />
              <span className="text-sm font-semibold leading-tight block">{p.title}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const AboutJourney = () => (
  <section className="py-24 sm:py-32 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-20">
        <SectionLabel>Who we serve</SectionLabel>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
          Secondary school through the steps right after.
        </h2>
        <p className="text-lg text-gray-600">
          From junior secondary to exams, scholarships, applications, and gap-year next steps.
        </p>
      </div>

      <div className="relative max-w-2xl mx-auto">
        <div className="absolute left-4 sm:left-1/2 sm:-translate-x-px top-0 bottom-0 w-0.5 bg-gradient-to-b from-skutopia-300 via-skutopia-500 to-skutopia-700" />
        {learnerJourney.map((stage, i) => (
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: i * 0.05, duration: 0.5 }}
            className={`relative flex items-center gap-6 mb-8 sm:mb-10 ${
              i % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"
            }`}
          >
            <div className="hidden sm:block sm:w-1/2" />
            <div className="absolute left-4 sm:left-1/2 sm:-translate-x-1/2 w-3 h-3 rounded-full bg-skutopia-500 ring-4 ring-skutopia-100 z-10" />
            <div
              className={`ml-12 sm:ml-0 sm:w-1/2 ${
                i % 2 === 0 ? "sm:pl-10 sm:text-left" : "sm:pr-10 sm:text-right"
              }`}
            >
              <div className="inline-block px-5 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-skutopia-200 transition-all">
                <span className="text-xs font-semibold text-skutopia-500 uppercase tracking-wider block mb-1">
                  Stage {i + 1}
                </span>
                <span className="text-base sm:text-lg font-semibold text-gray-900">{stage}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const AboutDifferent = () => (
  <section className="py-24 sm:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <SectionLabel>Why Skutopia</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-6">
          Learning and opportunity in one place.
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {philosophyContrast.map((col, ci) => (
          <motion.div
            key={col.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: ci * 0.1 }}
            className={`p-8 sm:p-10 rounded-3xl border ${
              ci === 1
                ? "bg-skutopia-500 text-white border-skutopia-500 shadow-xl"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3
              className={`text-sm font-semibold uppercase tracking-wider mb-6 ${
                ci === 1 ? "text-skutopia-100" : "text-gray-500"
              }`}
            >
              {col.label}
            </h3>
            <ul className="space-y-4">
              {col.points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span
                    className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${ci === 1 ? "bg-white" : "bg-gray-400"}`}
                  />
                  <span className={`text-base leading-relaxed ${ci === 1 ? "text-white/90" : "text-gray-700"}`}>
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const AboutValues = () => (
  <section className="py-24 sm:py-32 bg-gradient-to-b from-white to-skutopia-50/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <SectionLabel>Our values</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">What guides every decision.</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {values.map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="p-6 rounded-2xl bg-white/80 backdrop-blur border border-gray-100 hover:border-skutopia-200 hover:shadow-lg transition-all duration-300"
          >
            <h3 className="text-lg font-bold text-skutopia-700 mb-3">{v.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{v.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const AboutRoadmap = () => (
  <section className="py-24 sm:py-32 bg-skutopia-900 text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <SectionLabel>Roadmap</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6">
          Shipped today. Growing next.
        </h2>
        <p className="text-white/70 text-lg">
          We separate what learners can use now from what we are still building.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {roadmap.map((item, i) => (
          <motion.div
            key={item.phase}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur hover:bg-white/10 transition-colors"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-skutopia-300 mb-2">
              {item.period}
            </p>
            <h3 className="text-xl font-bold mb-2">{item.title}</h3>
            <p className="text-sm text-white/70 leading-relaxed">{item.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const AboutImpact = () => {
  const impactImageUrl = useMarketingImage("about.impact.image", studentsImage);
  const overrides = useMarketingAssets();

  return (
    <section className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <SectionLabel>Impact</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Confidence in exams. Doors opened in Discover.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We measure success in preparation that sticks and opportunities students would have missed.
            </p>
          </div>
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl">
            <img src={impactImageUrl} alt="Skutopia learners" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-skutopia-900/60 to-transparent" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {impactStories.map((story, i) => {
            const assetKey = TESTIMONIAL_ASSET_KEYS[story.name];
            const image = (assetKey && overrides[assetKey]?.url) || story.image;
            return (
              <motion.blockquote
                key={story.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 rounded-3xl bg-gray-50 border border-gray-100 flex flex-col"
              >
                <span className="inline-block self-start px-3 py-1 rounded-full bg-skutopia-100 text-skutopia-700 text-xs font-semibold uppercase tracking-wider mb-6">
                  {story.outcome}
                </span>
                <p className="text-gray-700 leading-relaxed flex-1 mb-6 italic">&ldquo;{story.quote}&rdquo;</p>
                <footer className="flex items-center gap-3">
                  <img
                    src={image}
                    alt={story.name}
                    className="h-12 w-12 rounded-full object-cover object-top ring-2 ring-white shadow-sm"
                  />
                  <div>
                    <cite className="not-italic font-bold text-gray-900 block">{story.name}</cite>
                    <span className="text-sm text-gray-500">{story.role}</span>
                  </div>
                </footer>
              </motion.blockquote>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const AboutFinalCTA = () => (
  <section className="py-28 sm:py-36 bg-gradient-to-br from-skutopia-600 via-skutopia-500 to-skutopia-700 text-white relative">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_45%)]" />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-skutopia-100 mb-6">
          Get started
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-8">
          Download Skutopia and start today.
        </h2>
        <p className="text-xl text-white/80 leading-relaxed mb-12 max-w-2xl mx-auto">
          Free on Google Play for high school learners across Africa.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full px-10 py-4 text-base font-medium border border-white/40 hover:bg-white/10 transition-colors"
          >
            Download the app
          </a>
          <button
            type="button"
            onClick={navigateToFlutterSignup}
            className="inline-flex items-center justify-center rounded-full px-10 py-4 text-base font-medium bg-white text-skutopia-700 hover:bg-skutopia-50 transition-colors"
          >
            Create an account
          </button>
        </div>
      </motion.div>
    </div>
  </section>
);
